/**
 * routeCatalog — ÚNICA fuente de verdad de las rutas protegidas del frontend:
 * path, roles permitidos, módulo de permiso y plan requerido.
 *
 * App.tsx genera las <Route> protegidas desde este catálogo y
 * navigation.config.ts deriva roles/permisos/plan del menú desde aquí.
 * Cambiar un permiso de ruta se hace SOLO en este archivo.
 */
import type { Permission } from "@/types";
import type { PlanType } from "@/config/plans/planAccess";
import {
  ALL_APP_ROLES,
  CASHIER_ROLES,
  DELIVERY_ROLES,
  OPERATIVE_ROLES,
  OWNER_ADMIN_ROLES,
  Role,
} from "@/config/roles";

export interface AppRoute {
  path: string;
  roles: Role[];
  /** Clave de permiso modular (opcional: rutas "Mi …" no usan permiso). */
  module?: keyof Permission;
  /** Metadato de plan (lo usan el menú y la UI; no restringe el acceso). */
  plan?: PlanType;
}

export const APP_ROUTES: AppRoute[] = [
  {
    path: "/dashboard",
    roles: OWNER_ADMIN_ROLES,
    module: "dashboard",
    plan: "basico",
  },
  {
    path: "/caja",
    roles: [...OWNER_ADMIN_ROLES, ...CASHIER_ROLES],
    module: "caja",
    plan: "basico",
  },
  {
    path: "/caja/ventas",
    roles: [...OWNER_ADMIN_ROLES, "cajero"],
    module: "caja_ventas",
    plan: "basico",
  },
  {
    path: "/caja/cierres",
    roles: [...OWNER_ADMIN_ROLES, "cajero"],
    module: "caja_cierres",
    plan: "basico",
  },
  {
    path: "/caja/movimientos",
    roles: [...OWNER_ADMIN_ROLES, "cajero"],
    module: "caja_movimientos",
    plan: "basico",
  },
  {
    path: "/caja/creditos",
    roles: [...OWNER_ADMIN_ROLES, "cajero"],
    module: "caja_creditos",
    plan: "basico",
  },
  {
    path: "/caja/fidelizacion",
    roles: [...OWNER_ADMIN_ROLES, "cajero"],
    module: "caja_fidelizacion",
    plan: "basico",
  },
  {
    path: "/inventario",
    roles: OWNER_ADMIN_ROLES,
    module: "inventario",
    plan: "basico",
  },
  {
    path: "/inventario/promociones",
    roles: OWNER_ADMIN_ROLES,
    module: "inventario",
    plan: "basico",
  },
  {
    path: "/personal",
    roles: OWNER_ADMIN_ROLES,
    module: "personal",
    plan: "profesional",
  },
  {
    path: "/personal/tareas",
    roles: OWNER_ADMIN_ROLES,
    module: "personal_tareas",
    plan: "profesional",
  },
  {
    path: "/personal/asistencia",
    roles: OWNER_ADMIN_ROLES,
    module: "personal_asistencia",
    plan: "profesional",
  },
  {
    path: "/personal/nomina",
    roles: OWNER_ADMIN_ROLES,
    module: "personal_nomina",
    plan: "profesional",
  },
  {
    path: "/personal/vacantes",
    roles: OWNER_ADMIN_ROLES,
    module: "personal_vacantes",
    plan: "profesional",
  },
  {
    path: "/personal/vacaciones",
    roles: OWNER_ADMIN_ROLES,
    module: "personal",
    plan: "profesional",
  },
  {
    path: "/personal/calendario",
    roles: OWNER_ADMIN_ROLES,
    module: "personal_calendario",
    plan: "profesional",
  },
  {
    path: "/calificaciones",
    roles: OWNER_ADMIN_ROLES,
    module: "calificaciones",
    plan: "empresarial",
  },
  {
    path: "/buzon-equipo",
    roles: ALL_APP_ROLES,
    module: "personal_buzon",
    plan: "profesional",
  },
  {
    path: "/higiene",
    roles: OWNER_ADMIN_ROLES,
    module: "higiene",
    plan: "empresarial",
  },
  {
    path: "/higiene/:areaId",
    roles: OWNER_ADMIN_ROLES,
    module: "higiene",
    plan: "empresarial",
  },
  {
    path: "/mis-tareas",
    roles: OPERATIVE_ROLES,
    plan: "profesional",
  },
  {
    path: "/mi-asistencia",
    roles: OPERATIVE_ROLES,
    plan: "profesional",
  },
  {
    path: "/mi-nomina",
    roles: OPERATIVE_ROLES,
    plan: "profesional",
  },
  {
    path: "/mi-perfil",
    roles: OPERATIVE_ROLES,
    plan: "profesional",
  },
  {
    path: "/auditoria",
    roles: ["dueño"],
    module: "auditoria",
    plan: "profesional",
  },
  {
    path: "/metricas",
    roles: OWNER_ADMIN_ROLES,
    module: "metricas",
    plan: "basico",
  },
  {
    // Página completa de análisis de una tarjeta de Métricas
    // (`/metricas/analisis/<cardId>`; el cardId va URL-encoded y puede contener
    // `:` como en `kpi:ventas_hoy`). Se declara antes que `:sectionId` para que
    // "analisis" nunca se interprete como una sección.
    path: "/metricas/analisis/:cardId",
    roles: OWNER_ADMIN_ROLES,
    module: "metricas",
    plan: "basico",
  },
  {
    // Secciones del hub de Métricas (`resumen`, `negocio`, `resultados`,
    // `rentabilidad`, `inventario`, `clientes`, `equipo`, `operacion`).
    // El id se valida en `MetricasSectionPage`; el catálogo de secciones vive en
    // `features/metrics/utils/metricsSections.ts`.
    path: "/metricas/:sectionId",
    roles: OWNER_ADMIN_ROLES,
    module: "metricas",
    plan: "basico",
  },
  {
    path: "/ia",
    roles: OWNER_ADMIN_ROLES,
    module: "ia",
    plan: "empresarial",
  },
  {
    // Pestaña concreta de IA (`/ia/copilot`, `/ia/anomalias`, ...) para poder
    // enlazar directo al chat del Copilot desde otras páginas. El id se valida
    // en `IAPage` (una pestaña inválida cae al resumen).
    path: "/ia/:tab",
    roles: OWNER_ADMIN_ROLES,
    module: "ia",
    plan: "empresarial",
  },
  {
    path: "/mesas",
    roles: [...OWNER_ADMIN_ROLES, ...OPERATIVE_ROLES],
    module: "mesas",
    plan: "profesional",
  },
  {
    path: "/mesas/ordenes",
    roles: [...OWNER_ADMIN_ROLES, ...OPERATIVE_ROLES],
    module: "mesas",
    plan: "profesional",
  },
  {
    path: "/mesas/cocina",
    roles: ALL_APP_ROLES,
    module: "cocina",
    plan: "profesional",
  },
  {
    path: "/mesas/configuracion",
    roles: OWNER_ADMIN_ROLES,
    module: "mesas_configuracion",
    plan: "profesional",
  },
  {
    path: "/tienda/pedidos",
    roles: [...OWNER_ADMIN_ROLES, ...DELIVERY_ROLES],
    module: "tienda",
    plan: "empresarial",
  },
  {
    path: "/tienda/configuracion",
    roles: OWNER_ADMIN_ROLES,
    module: "tienda_configuracion",
    plan: "empresarial",
  },
  {
    path: "/tienda/configuracion/asistente",
    roles: OWNER_ADMIN_ROLES,
    module: "tienda_configuracion",
    plan: "empresarial",
  },
  {
    path: "/tienda/liquidaciones",
    roles: [...OWNER_ADMIN_ROLES, ...DELIVERY_ROLES],
    module: "tienda_liquidaciones",
    plan: "empresarial",
  },
];

/** Lookup rápido por path (rutas protegidas). */
export function getAppRoute(path: string): AppRoute | undefined {
  return APP_ROUTES.find((r) => r.path === path);
}
